<template>
  <div class="properties-bar">
    <!---->
    <div class="properties-bar__button" :disabled="!isUndoable" @click="undo">
      <i class="material-icons">undo</i>
    </div>
    <!---->
    <div class="properties-bar__button" :disabled="!isRedoable" @click="redo">
      <i class="material-icons">redo</i>
    </div>
    <div
      v-tooltip.right-start="'Keyframes'"
      class="properties-bar__button"
      @click="setAnimationMode"
    >
      <i class="material-icons">{{animationMode ? 'toggle_on' : 'toggle_off'}}</i>
    </div>
    <div class="animation">
      <span>{{animationMode ? 'unlock' : 'lock'}}</span>
    </div>
    <!---->
    <div
      v-if="!visibilityOn"
      v-tooltip.right-start="'Change to arrange mode. ctrl+g'"
      class="properties-bar__button"
      @click="showArrangeView"
    >
      <i v-if="gridOn" class="material-icons">grid_on</i>
      <i v-else class="material-icons">grid_off</i>
    </div>
    <!---->
    <div class="wp" v-if="selectedWaypoint">
      <div class="txt-uppercase txt-bold">Waypoint&nbsp;</div>
      <div class="txt-uppercase pad-r-s pad-l-m">position</div>
      <div>x:</div>
      <input type="text" maxlength="4" :value="wpx" @input="onChangePosition('x', $event)" />
      <div>px</div>
      <div class="pad-l-s">y:</div>
      <input type="text" maxlength="4" :value="wpy" @input="onChangePosition('y', $event)" />
      <div>px</div>
      <div @click="onApplyPosition" v-tooltip.bottom-center="'apply position to all waypoints'">
        <i class="material-icons">format_paint</i>
      </div>
      <div class="properties-bar__separator">|</div>
      <div class="txt-uppercase pad-r-s pad-l-m">size</div>
      <div>w:</div>
      <input type="text" maxlength="4" :value="wpw" @input="onChangeSize('width', $event)" />
      <div>px</div>
      <div @click="locked = !locked" v-tooltip.bottom-center="'lock/unlock aspect ratio'">
        <i v-if="locked" class="material-icons">lock</i>
        <i v-else class="material-icons">lock_open</i>
      </div>
      <div>h:</div>
      <input type="text" maxlength="4" :value="wph" @input="onChangeSize('height', $event)" />
      <div>px</div>
      <div @click="onApplySize" v-tooltip.bottom-center="'apply size to all waypoints'">
        <i class="material-icons">format_paint</i>
      </div>
      <div class="properties-bar__separator">|</div>
      <div class="txt-uppercase pad-r-s pad-l-m">time</div>
      <div>t:</div>
      <time-input
        class="time-value"
        :currentTime="wpt"
        :frameRate="getFrameRate"
        @timeChange="(time) => onChangeTime(time)"
      />
      <div>{{getFrameRate}} fps</div>
      <div
        class="txt-uppercase txt-bold pad-l-m"
        v-tooltip.bottom-center="'use ALT-KEY while dragging or resizing a waypoint to snap to the Grid'"
      >Grid&nbsp;</div>
      <input
        type="number"
        maxlength="2"
        min="2"
        max="100"
        :value="grid.size"
        @input="onChangeGridSize($event)"
      />
      <div>px</div>
    </div>
  </div>
</template>

<script>
import { mapState, mapMutations, mapGetters } from "vuex";
import { undoRedo } from "../../mixins/undoRedo";
import timeInput from "../core/timeInput";

export default {
  name: "properties-bar",

  components: {
    "time-input": timeInput
  },

  mixins: [undoRedo],

  data: function() {
    return {
      locked: false,
      visibilityOn: false,
      gridOn: false,
      animation: false
    };
  },

  computed: {
    ...mapState(["video", "player", "selection", "grid", "animationMode"]),
    ...mapGetters(["getFrameRate", "videoDuration"]),

    selectedScene: function() {
      return this.selection.scene;
    },

    selectedWaypoint: function() {
      return this.selection.waypoint;
    },

    wpw: function() {
      return Math.min(
        this.video.dimensions.width,
        Math.round(this.video.dimensions.width * this.selectedWaypoint.pw)
      );
    },

    wph: function() {
      return Math.min(
        this.video.dimensions.height,
        Math.round(this.video.dimensions.height * this.selectedWaypoint.ph)
      );
    },

    wpx: function() {
      return Math.min(
        this.video.dimensions.width,
        Math.round(this.video.dimensions.width * this.selectedWaypoint.x)
      );
    },

    wpy: function() {
      return Math.min(
        this.video.dimensions.height,
        Math.round(this.video.dimensions.height * this.selectedWaypoint.y)
      );
    },

    wpt: function() {
      return this.selectedWaypoint.time;
    }
  },

  created() {
    this.keyEventUnsubscribe = this.keyEventSubscribe(["editor"], {
      "ctrl+z": this.undo,
      "ctrl+y": this.redo,
      "ctrl+g": this.showArrangeView
    });
  },

  beforeDestroy() {
    this.keyEventUnsubscribe();
  },

  methods: {
    ...mapMutations([
      "setPlayerTime",
      "emptyState",
      "updateGrid",
      "setMode",
      "setAnimationMode"
    ]),
    showArrangeView: function(e) {
      if (e) {
        e.preventDefault();
      }
      this.player.pause();
      this.gridOn = !this.gridOn;
      if (this.gridOn) {
        this.setMode("arranging");
      } else if (this.selection.tag) {
        this.setMode("editing");
      } else {
        this.setMode(null);
      }
      this.$emit("showArrangeView");
    },

    onChangeSize: function(prop, ev) {
      let val = ev.target.value;
      let vw = this.video.dimensions.width;
      let vh = this.video.dimensions.height;
      let wp = this.selectedWaypoint;

      if (prop === "width") {
        // normalize for invalid values
        if (isNaN(val) || val > vw) {
          ev.target.value = wp.w;
          return;
        }
        // contrain the new value
        let nw = Math.max(1, Math.min(vw, Math.round(val)));
        let ow = wp.w;
        // get the current factor we need for lock scaling the other dimension
        let fw = nw / ow;
        // normalize factor if needed
        if (isNaN(fw) || !isFinite(fw)) {
          fw = 1;
        }
        // if dimensions are locked, apply the factored value to the ohter dimension
        if (this.locked) {
          wp.h = (wp.h || nw) * fw;
          wp.ph = wp.h / vh;
        }
        // apply new values, absolute and in precent values for repsonsivenewss
        wp.w = nw;
        wp.pw = wp.w / vw;
      } else {
        if (isNaN(val) || val > vh) {
          ev.target.value = wp.h;
          return;
        }
        // contrain the new value
        let nh = Math.max(1, Math.min(vh, Math.round(val)));
        let oh = wp.h;
        // get the current factor we need for lock scaling the other dimension
        let fh = nh / oh;
        // normalize factor if needed
        if (isNaN(fh) || !isFinite(fh)) {
          fh = 1;
        }
        // if dimensions are locked, apply the factored value to the ohter dimension
        if (this.locked) {
          wp.w = (wp.w || nh) * fh;
          wp.pw = wp.w / vw;
        }
        // apply new values, absolute and in precent values for repsonsivenewss
        let h = Math.min(vh, Math.round(val));
        wp.h = h;
        wp.ph = wp.h / vh;
      }
    },

    onChangePosition: function(prop, ev) {
      let val = ev.target.value;
      let vw = this.video.dimensions.width;
      let vh = this.video.dimensions.height;
      let wp = this.selectedWaypoint;

      if (prop === "x") {
        // normalize for invalid values
        if (isNaN(val)) {
          ev.target.value = this.wpx;
          return;
        }
        // contrain the new value
        let nx = Math.max(0, Math.min(vw, Math.round(val)));
        ev.target.value = nx;
        wp.x = nx / vw;
      } else {
        // normalize for invalid values
        if (isNaN(val)) {
          ev.target.value = this.wpy;
          return;
        }
        // contrain the new value
        let ny = Math.max(0, Math.min(vh, Math.round(val)));
        ev.target.value = ny;
        wp.y = ny / vh;
      }
    },

    onChangeTime: function(time) {
      let wp = this.selectedWaypoint;

      wp.time = time > this.videoDuration ? this.videoDuration - 0.1 : time;
      this.setPlayerTime(time);
    },

    onChangeGridSize: function(ev) {
      this.updateGrid({
        size: ev.target.value
      });
    },

    onApplySize: function() {
      const pw = this.selection.waypoint.pw;
      const ph = this.selection.waypoint.ph;
      const w = this.selection.waypoint.w;
      const h = this.selection.waypoint.h;

      if (this.selection.scene.waypoints) {
        this.selection.scene.waypoints.map(wp => {
          wp.pw = pw;
          wp.ph = ph;
          wp.w = w;
          wp.h = h;
        });
      }
    },

    onApplyPosition: function() {
      const x = this.selection.waypoint.x;
      const y = this.selection.waypoint.y;

      if (this.selection.scene.waypoints) {
        this.selection.scene.waypoints.map(wp => {
          wp.x = x;
          wp.y = y;
        });
      }
    }
  }
};
</script>